import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import { Box, Breadcrumbs, Link, type SxProps, type Theme, Typography } from "@mui/material";
import React from "react";
import { useTranslation } from "react-i18next";
import { Link as RouterLink, useLocation } from "react-router-dom";

import { getAdminBreadcrumbs } from "./adminBreadcrumbs";
import { PageHeader } from "./PageHeader";

export interface PageShellProps {
  children: React.ReactNode;
  title: string;
  icon?: React.ReactNode;
  gradientTitle?: boolean;
  titleAccessory?: React.ReactNode;
  headerActions?: React.ReactNode;
  contextControls?: React.ReactNode;
  footer?: React.ReactNode;
  breadcrumbs?: "admin";
  bodySx?: SxProps<Theme>;
  className?: string;
}

/** Shared page frame for admin and customer insights/report pages. */
export const PageShell: React.FC<PageShellProps> = ({
  children,
  title,
  icon,
  gradientTitle,
  titleAccessory,
  headerActions,
  contextControls,
  footer,
  breadcrumbs,
  bodySx,
  className,
}) => {
  const location = useLocation();
  const { t } = useTranslation();
  const adminBreadcrumbs =
    breadcrumbs === "admin" ? getAdminBreadcrumbs(location.pathname, title, t) : null;

  return (
    <Box className={className}>
      <Box sx={{ px: 2.5 }}>
        {adminBreadcrumbs && (
          <Box sx={{ pt: 0, pb: 0, mb: 0 }}>
            <Breadcrumbs
              separator={<NavigateNextIcon fontSize="small" />}
              sx={{
                color: "text.primary",
                "& .MuiBreadcrumbs-ol": { flexWrap: "nowrap" },
                "& .MuiBreadcrumbs-li": {
                  display: "flex",
                  alignItems: "center",
                  minWidth: 0,
                },
              }}
            >
              {adminBreadcrumbs.map((breadcrumb, index) => {
                const isCurrentPage = index === adminBreadcrumbs.length - 1;
                const content =
                  index === 0 ? (
                    <>
                      <AdminPanelSettingsIcon sx={{ fontSize: "1em" }} />
                      <Box component="span">{breadcrumb.label}</Box>
                    </>
                  ) : (
                    breadcrumb.label
                  );
                if (breadcrumb.to) {
                  return (
                    <Link
                      key={breadcrumb.to}
                      component={RouterLink}
                      to={breadcrumb.to}
                      variant="body2"
                      color="text.primary"
                      underline="hover"
                      sx={{ "&:hover": { color: "primary.main" } }}
                    >
                      {content}
                    </Link>
                  );
                }
                return (
                  <Typography
                    key={breadcrumb.label}
                    component="span"
                    variant="body2"
                    color="text.primary"
                    noWrap={isCurrentPage}
                    sx={{
                      display: index === 0 ? "inline-flex" : undefined,
                      alignItems: "center",
                      gap: index === 0 ? 0.5 : undefined,
                      maxWidth: isCurrentPage ? { xs: 220, sm: 420 } : undefined,
                    }}
                  >
                    {content}
                  </Typography>
                );
              })}
            </Breadcrumbs>
          </Box>
        )}
        <PageHeader
          title={title}
          icon={icon}
          gradientTitle={gradientTitle}
          titleAccessory={titleAccessory}
          headerActions={headerActions}
        />
        {contextControls}
        <Box sx={bodySx}>{children}</Box>
        {footer}
      </Box>
    </Box>
  );
};
